import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, ApiError } from "@/lib/api";
import { oneOf } from "@/lib/searchParams";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { Pagination } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm } from "@/features/confirm/ConfirmProvider";
import { formatDate, formatMoney } from "@bookboost/shared/format";

interface CommissionRow {
  id: string;
  user_id: string | null;
  deal_id: string | null;
  invoice_id: string | null;
  deal_amount: number | null;
  commission_total: number;
  cash_portion: number;
  equity_portion: number;
  currency: string;
  status: "pending" | "earned" | "approved" | "paid";
  earned_at: string | null;
  approved_at: string | null;
  paid_at: string | null;
  reversal_of_id: string | null;
  users: { email: string; full_name: string | null; salary_currency: string | null } | null;
  deals: { deal_name: string | null } | null;
  invoices: { stripe_invoice_id: string } | null;
}

interface UserOption {
  id: string;
  email: string;
  full_name: string | null;
}

interface ListResponse {
  rows: CommissionRow[];
  users: UserOption[];
  total: number;
  page: number;
  pageSize: number;
  flaggedIds: string[];
}

const commissionsSearch = z.object({
  user: z.string().optional(),
  status: z.enum(["pending", "earned", "approved", "paid"]).optional(),
  q: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  min: z.string().optional(),
  max: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});

type SearchParams = z.infer<typeof commissionsSearch>;

export const Route = createFileRoute("/admin/commissions")({
  validateSearch: commissionsSearch,
  component: CommissionsPage,
});

function CommissionsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  // Local form state so the user can edit filters before applying.
  const [draft, setDraft] = useState<SearchParams>(search);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "commissions", search],
    queryFn: () =>
      api.get<ListResponse>("/api/admin/commissions", {
        searchParams: {
          user: search.user,
          status: search.status,
          q: search.q,
          from: search.from,
          to: search.to,
          min: search.min,
          max: search.max,
          page: search.page,
        },
      }),
  });

  // Deal / invoice / rep search is now resolved server-side (see the worker
  // route) so it composes with pagination.
  const rows = data?.rows ?? [];
  const flaggedSet = new Set(data?.flaggedIds ?? []);

  const hasFilters = Boolean(
    search.user || search.status || search.q || search.from || search.to || search.min || search.max,
  );

  function applyFilters() {
    // Strip empties so the URL stays clean. Building the object property-by-
    // property keeps SearchParams' enum-typed `status` field correctly typed —
    // a generic loop would force every value through `as never`.
    const clean: SearchParams = {
      user: draft.user || undefined,
      status: draft.status || undefined,
      q: draft.q || undefined,
      from: draft.from || undefined,
      to: draft.to || undefined,
      min: draft.min || undefined,
      max: draft.max || undefined,
    };
    navigate({ search: clean });
  }
  function resetFilters() {
    setDraft({});
    navigate({ search: {} });
  }

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["admin", "commissions"] });
    qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    qc.invalidateQueries({ queryKey: ["admin", "payouts"] });
    qc.invalidateQueries({ queryKey: ["finance"] });
  };

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/api/commissions/${id}/approve`),
    onSuccess: () => { toast.ok("Commission approved."); invalidateAll(); },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't approve."),
  });
  const unapprove = useMutation({
    mutationFn: (id: string) => api.post(`/api/commissions/${id}/unapprove`),
    onSuccess: () => { toast.ok("Reverted to earned."); invalidateAll(); },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't unapprove."),
  });
  const markPaid = useMutation({
    mutationFn: (id: string) => api.post(`/api/commissions/${id}/mark-paid`),
    onSuccess: () => { toast.ok("Commission marked paid."); invalidateAll(); },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't mark paid."),
  });
  const unpay = useMutation({
    mutationFn: (id: string) => api.post<{ nextStatus: "earned" | "approved" }>(`/api/commissions/${id}/unpay`),
    onSuccess: (res) => { toast.ok(`Reverted to ${res.nextStatus}.`); invalidateAll(); },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't revert."),
  });

  const csvHref = `/api/export/commissions.csv${buildCsvQuery(search)}`;

  return (
    <>
      <h1>Commissions</h1>

      <Panel>
        <form
          className="inline filters"
          onSubmit={(e) => {
            e.preventDefault();
            applyFilters();
          }}
        >
          <div className="field-group" style={{ minWidth: 260, flex: 1 }}>
            <label className="muted" htmlFor="q">Search</label>
            <input
              id="q"
              type="search"
              placeholder="deal / invoice id / rep"
              value={draft.q ?? ""}
              onChange={(e) => setDraft({ ...draft, q: e.target.value })}
            />
          </div>
          <div className="field-group">
            <label className="muted" htmlFor="rep-filter">Rep</label>
            <select
              id="rep-filter"
              value={draft.user ?? ""}
              onChange={(e) => setDraft({ ...draft, user: e.target.value || undefined })}
            >
              <option value="">All</option>
              {(data?.users ?? []).map((u) => (
                <option key={u.id} value={u.id}>{u.full_name ?? u.email}</option>
              ))}
            </select>
          </div>
          <div className="field-group">
            <label className="muted" htmlFor="status-filter">Status</label>
            <select
              id="status-filter"
              value={draft.status ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  status: oneOf(e.target.value, ["pending", "earned", "approved", "paid"] as const),
                })
              }
            >
              <option value="">All</option>
              {["pending", "earned", "approved", "paid"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="field-group">
            <label className="muted" htmlFor="from">From</label>
            <input
              id="from"
              type="date"
              value={draft.from ?? ""}
              onChange={(e) => setDraft({ ...draft, from: e.target.value || undefined })}
            />
          </div>
          <div className="field-group">
            <label className="muted" htmlFor="to">to</label>
            <input
              id="to"
              type="date"
              value={draft.to ?? ""}
              onChange={(e) => setDraft({ ...draft, to: e.target.value || undefined })}
            />
          </div>
          <div className="field-group">
            <label className="muted" htmlFor="min">Min</label>
            <input
              id="min"
              type="number"
              step="1"
              min="0"
              placeholder="0"
              value={draft.min ?? ""}
              onChange={(e) => setDraft({ ...draft, min: e.target.value || undefined })}
              style={{ width: "7em" }}
            />
          </div>
          <div className="field-group">
            <label className="muted" htmlFor="max">Max</label>
            <input
              id="max"
              type="number"
              step="1"
              min="0"
              placeholder="∞"
              value={draft.max ?? ""}
              onChange={(e) => setDraft({ ...draft, max: e.target.value || undefined })}
              style={{ width: "7em" }}
            />
          </div>
          <button type="submit" className="primary">Filter</button>
          {hasFilters && (
            <button type="button" onClick={resetFilters} className="reset">Reset</button>
          )}
          <div className="spacer" />
          <a href={csvHref} className="export-link">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M7 2v7m0 0l-3-3m3 3l3-3M2 12h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Export CSV
          </a>
        </form>
        <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          {isLoading ? "Loading…" : `${(data?.total ?? 0).toLocaleString()} commission${(data?.total ?? 0) === 1 ? "" : "s"} match.`}
        </p>
      </Panel>

      <Panel padding="tight">
        {error ? (
          <p className="err">{error instanceof Error ? error.message : "Failed to load."}</p>
        ) : !isLoading && rows.length === 0 ? (
          <EmptyState
            icon="commissions"
            title={hasFilters ? "No commissions match these filters" : "No commissions yet"}
            body={
              hasFilters
                ? "Try loosening filters, or reset to see everything."
                : "Commissions appear here when a Stripe invoice is marked paid for a linked deal."
            }
            actionHref={hasFilters ? "/admin/commissions" : undefined}
            actionLabel={hasFilters ? "Reset filters" : undefined}
          />
        ) : (
          <DataTable dense className="commissions-table">
            <table className="table-cards">
              <thead>
                <tr>
                  <th>Rep</th>
                  <th>Earned</th>
                  <th>Deal</th>
                  <th>Invoice</th>
                  <th className="num">Basis</th>
                  <th className="num">Total</th>
                  <th className="num">Cash</th>
                  <th className="num">Equity</th>
                  <th>Status</th>
                  <th>Paid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const reversal = Boolean(c.reversal_of_id);
                  const rep = c.users;
                  return (
                    <tr key={c.id} style={reversal ? { opacity: 0.6 } : undefined}>
                      <td data-label="Rep">
                        <Link
                          to="/admin/commissions/$id"
                          params={{ id: c.id }}
                          style={{
                            color: "var(--ink)",
                            fontWeight: 500,
                            textDecoration: "none",
                            borderBottom: "1px dashed transparent",
                          }}
                        >
                          {rep ? rep.full_name ?? rep.email : "—"}
                        </Link>
                      </td>
                      <td data-label="Earned" className="muted">{formatDate(c.earned_at)}</td>
                      <td data-label="Deal" title={c.deals?.deal_name ?? undefined}>{c.deals?.deal_name ?? "—"}</td>
                      <td data-label="Invoice" className="muted">
                        {c.invoices?.stripe_invoice_id ? (
                          <code title={c.invoices.stripe_invoice_id}>
                            {c.invoices.stripe_invoice_id.length > 11
                              ? `${c.invoices.stripe_invoice_id.slice(0, 10)}…`
                              : c.invoices.stripe_invoice_id}
                          </code>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td data-label="Basis" className="muted num">{formatMoney(c.deal_amount, c.currency)}</td>
                      <td data-label="Total" className="num">{formatMoney(c.commission_total, c.currency)}</td>
                      <td data-label="Cash" className="num">{formatMoney(c.cash_portion, c.currency)}</td>
                      <td data-label="Equity" className="num">{formatMoney(c.equity_portion, c.currency)}</td>
                      <td data-label="Status">
                        <StatusBadge status={c.status} suffix={reversal ? "(reversal)" : undefined} />
                        {flaggedSet.has(c.id) && (
                          <span className="badge" style={{ marginLeft: 4, background: "var(--warn-soft)", color: "var(--warn)" }} title="Rep flagged this for review">
                            ⚑ flagged
                          </span>
                        )}
                      </td>
                      <td data-label="Paid" className="muted">{formatDate(c.paid_at)}</td>
                      <td data-label="Actions">
                        {!reversal && c.status === "earned" && (
                          <button
                            type="button"
                            className="primary"
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            disabled={approve.isPending}
                            onClick={() => approve.mutate(c.id)}
                          >
                            Approve
                          </button>
                        )}
                        {!reversal && c.status === "approved" && (
                          <div style={{ display: "inline-flex", gap: 6 }}>
                            <button
                              type="button"
                              className="primary"
                              style={{ padding: "4px 10px", fontSize: 12 }}
                              disabled={markPaid.isPending}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: "Mark commission paid",
                                  prompt: `Mark ${formatMoney(c.cash_portion, c.currency)} cash for ${
                                    rep ? rep.full_name ?? rep.email : "this rep"
                                  } as paid? Only confirm after disbursing externally.`,
                                  confirmLabel: "Mark paid",
                                });
                                if (ok) markPaid.mutate(c.id);
                              }}
                            >
                              Mark paid
                            </button>
                            <button
                              type="button"
                              style={{ padding: "4px 10px", fontSize: 12 }}
                              disabled={unapprove.isPending}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: "Unapprove",
                                  prompt: "Revert this approval back to earned?",
                                  tone: "destructive",
                                  confirmLabel: "Unapprove",
                                });
                                if (ok) unapprove.mutate(c.id);
                              }}
                            >
                              Unapprove
                            </button>
                          </div>
                        )}
                        {!reversal && c.status === "paid" && (
                          <button
                            type="button"
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            disabled={unpay.isPending}
                            onClick={async () => {
                              const ok = await confirm({
                                title: "Revert paid",
                                prompt: "Revert this commission back to its previous state? Only do this if the payment was actually reversed.",
                                tone: "destructive",
                                confirmLabel: "Revert",
                              });
                              if (ok) unpay.mutate(c.id);
                            }}
                          >
                            Revert
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </DataTable>
        )}
        {data && data.total > 0 && (
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={(p) => navigate({ search: { ...search, page: p } })}
          />
        )}
      </Panel>

      <style>{`
        .filters { flex-wrap: wrap; gap: 14px; align-items: flex-end; }
        .field-group { display: flex; flex-direction: column; gap: 4px; min-width: 140px; }
        .field-group label { font-size: 11.5px; }
        .reset { color: var(--muted); font-size: 12.5px; padding: 8px 10px; background: transparent; border: 1px solid transparent; }
        .spacer { flex: 1; }
        .export-link {
          display: inline-flex; align-items: center; gap: 6px;
          color: var(--muted); font-size: 12.5px;
          padding: 6px 10px; border-radius: 6px;
        }
        .export-link:hover { color: var(--accent-deep); background: var(--primary50); text-decoration: none; }

        /* Eleven columns have to fit a laptop: one line per row, deal names
           ellipsized (full name in the title attr), money right-aligned. The
           wrapper still scrolls with shadow cues below ~1100px. */
        .commissions-table td, .commissions-table th { white-space: nowrap; }
        .commissions-table td[data-label="Deal"] {
          max-width: 18ch; overflow: hidden; text-overflow: ellipsis;
        }
        .commissions-table .num { text-align: right; font-variant-numeric: tabular-nums; }
      `}</style>
    </>
  );
}

function buildCsvQuery(search: SearchParams): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(search)) {
    if (v && String(v).length > 0) params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}
